import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';
import PageLoadError from '../components/PageLoadError';
import { useProfileContext } from '../hooks/useProfileContext';
import {
  apiGetShowRoadmap,
  apiGetShows,
  apiGetVenues,
  type RoadmapItemDTO,
  type ShowDTO,
  type VenueDTO,
} from '../api';
// P2e — "today" and the tour-stats badge are SHARED with the band landing and
// the Atlas (one algorithm, no second copy that could drift).
import { findShowToday, localDateKey, resolveNowIndex } from '../lib/dayOf';
import { tourStatsOf, tourStatsBadge } from '../lib/tourStats';
import './DayOfPage.css';

/**
 * P2d — Day of show / Run of show (maquette `sketches-refonte-ui-2026-10-09/005-vision-day-of`).
 *
 * The page that opens ON THE DAY OF A SHOW:
 *   - `.dayof-hero`  : giant mono date + local ticking clock + doors countdown;
 *   - `.dayof-ros`   : vertical run-of-show built from the show's REAL
 *                      `roadmap_items` (GET /api/shows/:id/roadmap), with a
 *                      pulsing NOW node + amber banner when the current hour
 *                      falls inside an item.
 * When the band has NO show today the page degrades to the Atlas "before"
 * pattern: next-show hero + countdown → clickable upcoming dates → link to the
 * Atlas. No NOW agenda outside the day itself (it would be meaningless).
 *
 * Rules honoured here (never broken):
 *   - NOTHING is invented: a field that does not exist simply is not rendered
 *     (`tour_dates` has no curfew column → the curfew line only appears when a
 *     roadmap item is really tagged `category='curfew'`; the masthead weather
 *     line is the STORED `weather` note typed by the tour manager — there is no
 *     live forecast widget anywhere);
 *   - "today" is computed from the CLIENT's local clock (`new Date()`), this
 *     page is a reading surface, nothing is persisted;
 *   - one 1 s interval for the clock/countdowns/NOW marker, cleared on unmount;
 *   - every visible string goes through `t()` (namespace `dayOf.*`, ×3 locales);
 *   - read-only: no backend call other than the existing reads, roadmap steps
 *     are NOT clickable (a RoadmapItemDTO carries no link/target to navigate to).
 */

type DayShow = ShowDTO & {
  /** Show start time — a real `tour_dates.time` column, returned by GET /api/shows. */
  time?: string;
};

type StepState = 'done' | 'now' | 'future' | 'plain';

interface Step {
  key: string;
  /** HH:MM shown in the time column ('' = the source carries no hour). */
  time: string;
  title: string;
  /** Real meta only: `location` · `notes`. */
  meta: string;
  /** Real chip only: `responsible`. */
  who: string;
  /** Minutes from midnight (null = no usable hour). */
  start: number | null;
  end: number | null;
  state: StepState;
  /** 0-100 progress of the current step, null when not computable. */
  progress: number | null;
}

const NOT_DATED = '9999-99-99';

const pad2 = (n: number) => String(Math.max(0, Math.floor(n))).padStart(2, '0');

/** 'HH:MM' → minutes from midnight, null when absent/invalid (never guessed). */
function toMin(value?: string | null): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(value ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** Local 'YYYY-MM-DD' for a Date — compared as a string, never via UTC parsing.
 *  (P2e: the rule moved to src/lib/dayOf.ts, shared with the band landing.) */

/** 'YYYY-MM-DD' → LOCAL Date (`new Date('2026-10-09')` would parse as UTC). */
function localFromKey(key: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key || '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Minutes from midnight of `ts` (fractional — seconds count for the progress bar). */
function nowMinutes(ts: number): number {
  const d = new Date(ts);
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
}

export default function DayOfPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { bandSlug } = useParams<{ bandSlug: string }>();
  const { profiles, activeProfileId } = useProfileContext();

  // URL slug wins (the band selector navigates to /band/:slug/…), the active
  // profile is the fallback — same rule as AtlasPage.
  const band = useMemo(() => {
    const fromUrl = bandSlug ? profiles.find((p) => p.slug === bandSlug) ?? null : null;
    const fromCtx = activeProfileId ? profiles.find((p) => p.id === activeProfileId) ?? null : null;
    return fromUrl || fromCtx;
  }, [profiles, bandSlug, activeProfileId]);
  const bandId = band?.id || '';

  const [shows, setShows] = useState<DayShow[]>([]);
  const [venues, setVenues] = useState<VenueDTO[]>([]);
  const [roadmap, setRoadmap] = useState<RoadmapItemDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // No band-scoped request may leave before the scope id is resolved.
  //
  // Lot 3a — `reloadToken` re-arms this effect for the shared error state's
  // Retry (a re-fetch, never a hard page reload). Chain and data unchanged.
  const [reloadToken, setReloadToken] = useState(0);
  useEffect(() => {
    if (!bandId) {
      setShows([]);
      setVenues([]);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([apiGetShows({ band: bandId }), apiGetVenues()])
      .then(([s, v]) => {
        if (cancelled) return;
        setShows((Array.isArray(s) ? s : []) as DayShow[]);
        setVenues(Array.isArray(v) ? v : []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bandId, reloadToken]);

  const todayId = useMemo(() => findShowToday(shows, now)?.id ?? null, [shows, now]);

  // Run of show of TODAY's show only — the request is dropped the moment the
  // id disappears (band change, midnight, page leaving the show-day mode).
  useEffect(() => {
    if (!todayId) {
      setRoadmap([]);
      return;
    }
    let cancelled = false;
    apiGetShowRoadmap(todayId)
      .then((r) => {
        if (!cancelled) setRoadmap(Array.isArray(r) ? r : []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setRoadmap([]);
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [todayId]);

  // The only timer on this page: clock, countdowns and the NOW marker.
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  const todayKey = localDateKey(new Date(now));
  const sorted = [...shows].sort((a, b) => {
    const ka = a.date || NOT_DATED;
    const kb = b.date || NOT_DATED;
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
  const isPast = (s: DayShow) => !!s.date && s.date < todayKey;
  const upcoming = sorted.filter((s) => s.date && !isPast(s));
  const todayShow = findShowToday(sorted, now);
  const nextShow = upcoming[0] ?? null;

  const venueById = useMemo(() => new Map(venues.map((v) => [String(v.id), v])), [venues]);
  const openShow = (id: string | number) => navigate(`/tour/shows/${String(id)}`);

  // Salle RÉELLE uniquement : pas de repli sur le nom de l'événement, sinon un jour de
  // voyage affiche « Travel to Helsinki » comme s'il s'agissait d'une salle (2026-11-03).
  const venueNameOf = (s?: DayShow | null) =>
    (s ? venueById.get(String(s.venue_id ?? ''))?.name || s.venue_name || '' : '');
  const placeOf = (s?: DayShow | null) =>
    s ? [s.city || venueById.get(String(s.venue_id ?? ''))?.city || '', s.country || venueById.get(String(s.venue_id ?? ''))?.country || ''].filter(Boolean).join(' ') : '';

  const dateLong = (key?: string) => {
    const d = key ? localFromKey(key) : null;
    return d ? d.toLocaleDateString(i18n.language, { weekday: 'short', day: '2-digit', month: 'short' }) : '';
  };

  /* ------------------------------------------------------------------ */
  /* Run-of-show steps — REAL roadmap items, ordered by hour; untimed     */
  /* items keep their place after the timed ones (nothing is ever dropped). */
  /* When the roadmap is EMPTY the steps are derived from the show's own   */
  /* fields only: Doors (door_time) and Show (time) — nothing else is      */
  /* ever fabricated (no invented "Load-in 14:00").                        */
  /* ------------------------------------------------------------------ */
  const steps: Step[] = [];
  {
    const currentMinutes = nowMinutes(now);
    const timed: RoadmapItemDTO[] = [];
    const untimed: RoadmapItemDTO[] = [];
    for (const it of roadmap) {
      if (toMin(it.time_from) != null) timed.push(it);
      else untimed.push(it);
    }
    timed.sort((a, b) => {
      const d = (toMin(a.time_from) ?? 0) - (toMin(b.time_from) ?? 0);
      return d !== 0 ? d : (a.sort_order ?? 0) - (b.sort_order ?? 0);
    });
    untimed.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    type Draft = Omit<Step, 'state' | 'progress'> & { started: boolean; ended: boolean };
    const drafts: Draft[] = [];

    const pushShowFallback = () => {
      // Only real show fields. `curfew` is NOT a tour_dates column — it is
      // never derived here (the masthead reads it from a tagged roadmap item).
      if (todayShow?.door_time && toMin(todayShow.door_time) != null) {
        const start = toMin(todayShow.door_time) as number;
        drafts.push({
          key: 'show-doors',
          time: todayShow.door_time,
          title: t('dayOf.stepDoors'),
          meta: '',
          who: '',
          start,
          end: null,
          started: start <= currentMinutes,
          ended: false,
        });
      }
      if (todayShow?.time && toMin(todayShow.time) != null) {
        const start = toMin(todayShow.time) as number;
        drafts.push({
          key: 'show-start',
          time: todayShow.time,
          title: t('dayOf.stepShow'),
          meta: '',
          who: '',
          start,
          end: null,
          started: start <= currentMinutes,
          ended: false,
        });
      }
    };

    if (roadmap.length === 0) {
      pushShowFallback();
    } else {
      for (const it of timed) {
        const start = toMin(it.time_from) as number;
        const end = toMin(it.time_to);
        drafts.push({
          key: String(it.id),
          time: String(it.time_from || ''),
          title: it.activity || '',
          meta: [it.location, it.notes].filter(Boolean).join(' · '),
          who: it.responsible || '',
          start,
          end,
          started: start <= currentMinutes,
          ended: end != null && currentMinutes >= end,
        });
      }
      for (const it of untimed) {
        drafts.push({
          key: String(it.id),
          time: '',
          title: it.activity || '',
          meta: [it.location, it.notes].filter(Boolean).join(' · '),
          who: it.responsible || '',
          start: null,
          end: null,
          started: false,
          ended: false,
        });
      }
      // No usable hour anywhere → the show's own fields fill the gap (top).
      if (drafts.every((d) => d.start == null)) {
        const before = drafts.length;
        pushShowFallback();
        const derived = drafts.splice(before);
        drafts.unshift(...derived);
      }
    }

    // NOW = the last started step that has not ended, resolved by the shared
    // pure helper (src/lib/dayOf.ts). A step without an end stays current until
    // the next one starts (spec P2d §2); a FINAL open-ended step keeps NOW for
    // LAST_OPEN_ENDED_WINDOW_MIN after its own start, then the run is done —
    // it must never stay "now" indefinitely (real case: Sound curfew with only
    // a start hour). No clock time is ever invented: time_from + window only.
    const nowIdx = resolveNowIndex(drafts, currentMinutes);
    drafts.forEach((d, i) => {
      const state: StepState =
        d.start == null ? 'plain' : i === nowIdx ? 'now' : d.started ? 'done' : 'future';
      let progress: number | null = null;
      if (state === 'now' && d.start != null && d.end != null && d.end > d.start) {
        progress = Math.min(100, Math.max(0, ((currentMinutes - d.start) / (d.end - d.start)) * 100));
      }
      steps.push({
        key: d.key,
        time: d.time,
        title: d.title,
        meta: d.meta,
        who: d.who,
        start: d.start,
        end: d.end,
        state,
        progress,
      });
    });
  }
  const nowStep = steps.find((s) => s.state === 'now') ?? null;

  // Real curfew only: a roadmap item tagged `curfew` carries the hour.
  const curfewTime =
    roadmap.find((r) => String(r.category || '').toLowerCase() === 'curfew' && toMin(r.time_from) != null)
      ?.time_from || '';

  /* --------------------------- masthead ------------------------------ */
  // Libellé d'état quand il n'y a pas de concert aujourd'hui : il suit le TYPE de la
  // prochaine date, car un jour de voyage / studio / off n'est pas « pas de concert » (2026-11-03).
  const STATE_KEYS: Record<string, string> = {
    travel: 'travel', studio: 'studio', rehearsal: 'rehearsal', off: 'off', promo: 'promo',
  };
  const nextStateLabel = (eventType?: string): string => {
    const key = STATE_KEYS[String(eventType || '').toLowerCase()];
    return key ? t(`dayOf.state.${key}`) : t('dayOf.noShowToday');
  };

  const todayDate = todayShow ? localFromKey(todayShow.date || '') : null;
  const dow = todayDate ? todayDate.toLocaleDateString(i18n.language, { weekday: 'short' }) : '';
  const mon = todayDate ? todayDate.toLocaleDateString(i18n.language, { month: 'short' }) : '';
  const dayNum = todayDate ? todayDate.toLocaleDateString(i18n.language, { day: 'numeric' }) : '';
  const nowDate = new Date(now);
  const clockTime = `${pad2(nowDate.getHours())}:${pad2(nowDate.getMinutes())}`;
  const clockSec = pad2(nowDate.getSeconds());

  const todayVenue = venueNameOf(todayShow);
  const todayPlace = placeOf(todayShow);
  const doorMin = todayShow ? toMin(todayShow.door_time) : null;
  const doorsRemain =
    todayShow && todayShow.door_time && doorMin != null
      ? Math.max(
          0,
          (() => {
            const base = localFromKey(todayShow.date || '');
            if (!base) return 0;
            base.setHours(Math.floor(doorMin / 60), Math.floor(doorMin % 60), 0, 0);
            return base.getTime() - now;
          })(),
        )
      : null;

  const subSegments: { key: string; node: ReactNode }[] = [
    { key: 'label', node: t('dayOf.mastLabel') },
  ];
  if (todayVenue) subSegments.push({ key: 'venue', node: <b>{todayVenue}</b> });
  if (todayPlace) subSegments.push({ key: 'place', node: todayPlace });
  if (todayShow?.door_time) subSegments.push({ key: 'doors', node: t('dayOf.doors', { time: todayShow.door_time }) });
  if (todayShow?.time) subSegments.push({ key: 'show', node: t('dayOf.showAt', { time: todayShow.time }) });
  if (curfewTime) subSegments.push({ key: 'curfew', node: t('dayOf.curfew', { time: curfewTime }) });

  /* ------------------- next show countdown (before mode) ------------- */
  const nextCountdown = (() => {
    if (!nextShow?.date) return null;
    const hm = /^(\d{1,2}:\d{2})/.exec(String(nextShow.door_time || ''));
    const base = localFromKey(nextShow.date);
    if (!base) return null;
    if (hm) {
      const [h, m] = hm[1].split(':').map(Number);
      base.setHours(h, m, 0, 0);
    }
    const remain = Math.max(0, base.getTime() - now);
    return {
      d: Math.floor(remain / 86400000),
      h: Math.floor(remain / 3600000) % 24,
      m: Math.floor(remain / 60000) % 60,
      s: Math.floor(remain / 1000) % 60,
    };
  })();

  const base = bandSlug ? `/band/${bandSlug}` : band?.slug ? `/band/${band.slug}` : '';
  const showDayMode = !!todayShow;

  // P2e — the BEFORE mode carries the Atlas tour-stats badge (same numbers,
  // same strings: the calculation lives in src/lib/tourStats.ts, no copy).
  // Never over the show-day hero: that night IS the show, the tour tally
  // would only add noise above the one thing that matters.
  const stats = useMemo(() => tourStatsOf(shows, venues), [shows, venues]);
  const statsBadge = tourStatsBadge(stats, i18n.language);

  return (
    <div className="page dayof-page" data-page="day-of" data-dayof-mode={showDayMode ? 'showday' : 'before'}>
      <PageHeader
        title={withPageIcon('dayOf.title', t('dayOf.title'))}
        subtitle={band?.name}
        meta={
          bandId && !showDayMode && stats.stops > 0 ? (
            <span className="dayof-headstats" data-dayof-headstats>
              {t(statsBadge.key, statsBadge.vars)}
            </span>
          ) : null
        }
        actions={
          bandId && !showDayMode ? (
            <button type="button" className="btn" data-dayof-atlas onClick={() => navigate(`${base}/atlas`)}>
              {t('dayOf.seeAtlas')}
            </button>
          ) : null
        }
      />

      {/* Lot 3a: the shows+venues read failed → no hero, no run of show, no
          dates. The shared state says so and its Retry RE-FETCHES (the page's
          own `.dayof-error` line could only display the message). */}
      {error && (
        <PageLoadError
          title={t('dayOf.title')}
          detail={error}
          onRetry={() => setReloadToken((n) => n + 1)}
        />
      )}

      {error ? null : !bandId ? (
        <p className="dayof-empty" data-dayof-empty>
          {t('dayOf.noBand')}
        </p>
      ) : loading ? (
        <p className="text-dim">{t('common.loading')}</p>
      ) : sorted.length === 0 ? (
        <p className="dayof-empty" data-dayof-empty>
          {t('dayOf.noShows')}
        </p>
      ) : showDayMode && todayShow ? (
        /* =================== MODE · DAY OF SHOW ====================== */
        <div className="dayof-stack">
          <section className="dayof-hero" data-dayof-hero>
            <div className="dayof-mast" data-dayof-mast>
              <div className="dayof-mast-left">
                <div className="dayof-big" data-dayof-date>
                  <span className="dow">{dow}</span> <span className="dnum">{dayNum}</span>{' '}
                  <span className="mon">{mon}</span>
                </div>
                <div className="dayof-sub" data-dayof-sub>
                  {subSegments.map((s, i) => (
                    <Fragment key={s.key}>
                      {i > 0 && <span className="sep"> · </span>}
                      {s.node}
                    </Fragment>
                  ))}
                </div>
              </div>
              <div className="dayof-mast-right">
                <div className="dayof-clock" data-dayof-clock>
                  {clockTime}
                  <small>:{clockSec}</small>
                </div>
                {/* Stored `tour_dates.weather` note typed by the tour manager —
                    never a live forecast, hidden when empty. */}
                {todayShow.weather && (
                  <div className="dayof-wx" data-dayof-wx>
                    {todayShow.weather}
                  </div>
                )}
              </div>
            </div>

            {doorsRemain != null && (
              <div className="dayof-doors" data-dayof-cdoors>
                <div className="cap">{t('dayOf.doorsIn')}</div>
                <div className="u">
                  <div className="n" data-dayof-cdoors-h>
                    {pad2(doorsRemain / 3600000)}
                  </div>
                  <div className="l">{t('dayOf.hours')}</div>
                </div>
                <div className="u">
                  <div className="n" data-dayof-cdoors-m>
                    {pad2((doorsRemain / 60000) % 60)}
                  </div>
                  <div className="l">{t('dayOf.minutes')}</div>
                </div>
                <div className="u">
                  <div className="n" data-dayof-cdoors-s>
                    {pad2((doorsRemain / 1000) % 60)}
                  </div>
                  <div className="l">{t('dayOf.seconds')}</div>
                </div>
              </div>
            )}
          </section>

          <section className="dayof-ros" data-dayof-ros>
            <div className="dayof-ros-hd">
              <div className="t">{t('dayOf.agenda')}</div>
              {nowStep && (
                <div className="dayof-live" data-dayof-live>
                  <span className="led" />
                  {t('dayOf.now')} · {nowStep.title}
                </div>
              )}
            </div>

            {steps.length === 0 ? (
              <p className="dayof-ros-empty" data-dayof-ros-empty>
                {t('dayOf.noAgenda')}
              </p>
            ) : (
              <div className="dayof-agenda">
                {steps.map((st) => (
                  <Fragment key={st.key}>
                    {st.state === 'now' && (
                      <div className="dayof-nowbar" data-dayof-nowbar>
                        <span className="lbl">
                          <span className="led" />
                          {t('dayOf.now')}
                        </span>
                        <span className="txt">
                          <b>{st.title}</b>
                          {st.time && <> · {t('dayOf.startedAt', { time: st.time })}</>}
                        </span>
                        {st.progress != null && (
                          <span className="prog" data-dayof-progress>
                            <i style={{ width: `${Math.round(st.progress)}%` }} />
                          </span>
                        )}
                      </div>
                    )}
                    {/* A roadmap item has no link/target field → steps are NOT clickable. */}
                    <div
                      className={`dayof-step ${st.state}`}
                      data-dayof-step={st.key}
                      data-dayof-step-state={st.state}
                    >
                      <div className="t">{st.time}</div>
                      <div className="rail">
                        <span className="node" />
                      </div>
                      <div className="body">
                        <div className="name">{st.title}</div>
                        {st.meta && <div className="meta">{st.meta}</div>}
                      </div>
                      {st.who && (
                        <div className="who">
                          <span className="c">{st.who}</span>
                        </div>
                      )}
                    </div>
                  </Fragment>
                ))}
              </div>
            )}

            <div className="dayof-ros-ft">
              <button
                type="button"
                className="btn btn-primary"
                data-dayof-open
                onClick={() => openShow(todayShow.id)}
              >
                {t('dayOf.openShow')}
              </button>
            </div>
          </section>
        </div>
      ) : (
        /* =================== MODE · BEFORE (no show today) ============= */
        <div className="dayof-stack">
          {nextShow && nextCountdown ? (
            <section className="dayof-next" data-dayof-hero>
              <div className="dayof-next-body">
                <div className="dayof-next-state" data-dayof-state>
                  <span className="led" />
                  <span>
                    {nextStateLabel(nextShow.event_type)}
                    {nextShow.date ? ` · ${dateLong(nextShow.date)}` : ''}
                  </span>
                </div>
                {/* La salle n'apparaît que si elle est spécifiée : un jour de voyage,
                    studio, off ou promo n'en a pas (2026-11-03). */}
                {venueNameOf(nextShow) ? (
                  <div className="dayof-next-venue" data-dayof-venue>
                    {venueNameOf(nextShow)}
                  </div>
                ) : null}
                <div className="dayof-next-meta">
                  {[dateLong(nextShow.date), placeOf(nextShow), nextShow.door_time ? t('dayOf.doors', { time: nextShow.door_time }) : '']
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                <div className="dayof-next-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-dayof-open
                    onClick={() => openShow(nextShow.id)}
                  >
                    {t('dayOf.openShow')}
                  </button>
                </div>
              </div>
              <div className="dayof-next-cd">
                <div className="dayof-capline">
                  {venueNameOf(nextShow)
                    ? t('dayOf.nextShowIn', { venue: venueNameOf(nextShow) })
                    : t('dayOf.nextEventIn')}
                </div>
                <div className="dayof-cd" data-dayof-cd>
                  <div className="u">
                    <div className="n" data-dayof-cd-d>
                      {pad2(nextCountdown.d)}
                    </div>
                    <div className="l">{t('dayOf.days')}</div>
                  </div>
                  <div className="u">
                    <div className="n" data-dayof-cd-h>
                      {pad2(nextCountdown.h)}
                    </div>
                    <div className="l">{t('dayOf.hours')}</div>
                  </div>
                  <div className="u">
                    <div className="n" data-dayof-cd-m>
                      {pad2(nextCountdown.m)}
                    </div>
                    <div className="l">{t('dayOf.minutes')}</div>
                  </div>
                  <div className="u">
                    <div className="n" data-dayof-cd-s>
                      {pad2(nextCountdown.s)}
                    </div>
                    <div className="l">{t('dayOf.seconds')}</div>
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          <section className="dayof-card" data-dayof-listcard>
            <div className="dayof-card-hd">
              <div className="t">{t('dayOf.upcoming')}</div>
            </div>
            {upcoming.length === 0 ? (
              <p className="dayof-list-empty" data-dayof-list-empty>
                {t('dayOf.noUpcoming')}
              </p>
            ) : (
              <div className="dayof-list" data-dayof-list>
                {upcoming.map((s) => {
                  const id = String(s.id);
                  const d = localFromKey(s.date || '');
                  const nextId = nextShow ? String(nextShow.id) : '';
                  return (
                    <button
                      key={id}
                      type="button"
                      className="dayof-row"
                      data-dayof-row={id}
                      onClick={() => openShow(s.id)}
                    >
                      <span className="dt">
                        {d ? d.toLocaleDateString(i18n.language, { day: '2-digit' }) : ''}
                        <small>{d ? d.toLocaleDateString(i18n.language, { month: 'short' }) : ''}</small>
                      </span>
                      <span className="mid">
                        {/* Pas de salle (voyage, studio, off…) ⇒ le nom de la date, jamais un tiret. */}
                        <span className="v">{venueNameOf(s) || s.name || '—'}</span>
                        <span className="c">{placeOf(s)}</span>
                      </span>
                      <span className={`dayof-tag${id === nextId ? ' next' : ''}`}>
                        {id === nextId ? t('dayOf.nextShow') : s.status || ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
